/**
 * Warns that Feature Management shows entity names translated at the read
 * boundary while rename inputs still write the canonical English name.
 *
 * The backend substitutes sport/league/market names for the bound locale, so
 * an operator on a non-`en` language sees translations in the same fields the
 * rename modals prefill from. There is no structural guard against submitting
 * a translated name back as English — this notice is the agreed mitigation.
 *
 * Shown once per browser session per surface: the hazard is worth stating on
 * arrival, not on every row or re-render.
 */

import { message } from "antd";
import i18n from "../../../i18n";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { DEFAULT_LANGUAGE } from "../../../utils/i18n/supportedLanguages";

const SESSION_KEY_PREFIX = "sportsbook_fm_translated_names_notice";

export const SPORTSBOOK_FM_SURFACE = "shell";

// In-memory guard so the notice fires at most once per page load even when
// sessionStorage throws (private modes); storage extends that across reloads.
const shownInMemory = new Set<string>();

const alreadyShown = (surface: string): boolean => {
  if (shownInMemory.has(surface)) {
    return true;
  }
  try {
    return (
      window.sessionStorage?.getItem(`${SESSION_KEY_PREFIX}:${surface}`) ===
      "true"
    );
  } catch {
    return false;
  }
};

const markShown = (surface: string): void => {
  shownInMemory.add(surface);
  try {
    window.sessionStorage?.setItem(`${SESSION_KEY_PREFIX}:${surface}`, "true");
  } catch {
    // ignore sessionStorage errors
  }
};

/** Test seam: the in-memory guard outlives sessionStorage.clear(). */
export const resetTranslatedNamesNoticeForTests = (): void => {
  shownInMemory.clear();
};

export function showTranslatedNamesNotice(
  surface: string = SPORTSBOOK_FM_SURFACE
): boolean {
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  if (!activeLanguage || activeLanguage === DEFAULT_LANGUAGE) {
    return false;
  }

  if (alreadyShown(surface)) {
    return false;
  }

  markShown(surface);
  message.warning(AppStrings.SPORTSBOOK_FM_TRANSLATED_NAMES_WARNING);
  return true;
}
